import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { MAX_SIZES } from '../constants/defaults';

const STATIC_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const IMAGE_OR_GIF_TYPES = [...STATIC_IMAGE_TYPES, 'image/gif'];

/**
 * Servicio de manejo de imágenes con Firebase Storage
 */

/**
 * Redimensionar imagen usando Canvas
 * @param {File} file - Archivo de imagen
 * @param {number} maxWidth - Ancho máximo
 * @param {number} maxHeight - Alto máximo
 * @param {number} quality - Calidad (0-1)
 * @returns {Promise<Blob>} - Imagen redimensionada
 */
export const resizeImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        // Calcular nuevas dimensiones manteniendo aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        // Crear canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir a blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Error al crear blob de imagen'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Error al cargar imagen'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Error al leer archivo'));
    reader.readAsDataURL(file);
  });
};

/**
 * Subir imagen de perfil a Firebase Storage
 * @param {File} file - Archivo de imagen
 * @param {string} userId - ID del usuario
 * @param {string} type - Tipo de imagen: 'avatar' o 'banner' (default: 'avatar')
 * @returns {Promise<string>} - URL de la imagen subida
 */
export const uploadProfileImage = async (file, userId, type = 'avatar') => {
  try {
    // Validar tamaño
    const maxSize = type === 'banner' ? MAX_SIZES.BANNER : MAX_SIZES.AVATAR;
    if (file.size > maxSize) {
      throw new Error(`La imagen debe pesar menos de ${maxSize / (1024 * 1024)}MB`);
    }
    
    // Validar tipo
    if (!file.type.startsWith('image/')) {
      throw new Error('El archivo debe ser una imagen');
    }
    
    // Dimensiones según tipo
    const dimensions = type === 'banner' 
      ? { width: 1200, height: 400 } 
      : { width: 500, height: 500 };
    
    // Redimensionar imagen
    const resizedBlob = await resizeImage(file, dimensions.width, dimensions.height, 0.9);
    
    // Generar nombre único
    // IMPORTANTE: debe coincidir con storage.rules (banners/{userId}/{imageId} y profile_images/{userId}/{imageId})
    const folder = type === 'banner' ? 'banners' : 'profile_images';
    const fileName = `${userId}_${uuidv4()}.jpg`;
    const storageRef = ref(storage, `${folder}/${userId}/${fileName}`);
    
    // Subir imagen
    const snapshot = await uploadBytes(storageRef, resizedBlob);
    
    // Obtener URL de descarga
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      success: true,
      url: downloadURL,
      path: snapshot.ref.fullPath
    };
  } catch (error) {
    console.error('Error al subir imagen:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eliminar imagen de Firebase Storage
 * @param {string} imageUrl - URL de la imagen a eliminar
 * @returns {Promise<object>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    if (!imageUrl || typeof imageUrl !== 'string') {
      return { success: true, skipped: true };
    }

    let url;
    try {
      url = new URL(imageUrl);
    } catch {
      // URL legacy o inválida → no intentamos borrar
      return { success: true, skipped: true };
    }

    // Solo intentamos borrar si es una URL de Firebase Storage
    const host = (url.hostname || '').toLowerCase();
    if (!host.includes('firebasestorage.googleapis.com')) {
      return { success: true, skipped: true };
    }

    // Extraer path de la URL (formato /v0/b/<bucket>/o/<encodedPath>)
    const parts = url.pathname.split('/o/');
    if (parts.length < 2) {
      return { success: true, skipped: true };
    }

    const encodedPath = parts[1];
    const imagePath = decodeURIComponent(encodedPath);
    const imageRef = ref(storage, imagePath);
    
    await deleteObject(imageRef);
    
    return { success: true };
  } catch (error) {
    // No romper el flujo de update de perfil/banner por un borrado fallido
    console.warn('No se pudo eliminar imagen anterior:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subir imagen general (posts, banners, etc)
 * @param {File} file - Archivo de imagen
 * @param {string} path - Ruta donde guardar (ej: 'posts', 'banners')
 * @param {string} userId - ID del usuario
 * @returns {Promise<object>}
 */
export const uploadImage = async (file, path, userId) => {
  try {
    // Validar tamaño
    if (file.size > MAX_SIZES.IMAGE) {
      throw new Error(`La imagen debe pesar menos de ${MAX_SIZES.IMAGE / (1024 * 1024)}MB`);
    }
    
    // Validar tipo
    if (!file.type.startsWith('image/')) {
      throw new Error('El archivo debe ser una imagen');
    }
    
    // GIFs: no redimensionar para preservar la animación (resizeImage convierte a JPEG)
    const isGif = file.type === 'image/gif';
    let fileToUpload = file;
    if (!isGif && file.size > 1024 * 1024) { // Si es mayor a 1MB y no GIF, redimensionar
      fileToUpload = await resizeImage(file, 1200, 1200, 0.85);
    }

    // Generar nombre único (GIF: .gif para preservar animación)
    const ext = isGif ? '.gif' : '.jpg';
    const fileName = `${userId}_${uuidv4()}${ext}`;
    const storageRef = ref(storage, `${path}/${fileName}`);
    
    // Subir imagen
    const snapshot = await uploadBytes(storageRef, fileToUpload);
    
    // Obtener URL de descarga
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      success: true,
      url: downloadURL,
      path: snapshot.ref.fullPath
    };
  } catch (error) {
    console.error('Error al subir imagen:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subir video a Firebase Storage (posts)
 * @param {File} file - Archivo de video
 * @param {string} path - Ruta donde guardar (ej: 'posts/userId')
 * @param {string} userId - ID del usuario
 * @returns {Promise<object>}
 */
export const uploadVideo = async (file, path, userId) => {
  try {
    if (!file.type.startsWith('video/')) {
      throw new Error('El archivo debe ser un video');
    }
    if (file.size > MAX_SIZES.VIDEO) {
      throw new Error(`El video debe pesar menos de ${MAX_SIZES.VIDEO / (1024 * 1024)}MB`);
    }
    const extMap = {
      'video/mp4': '.mp4',
      'video/webm': '.webm',
      'video/quicktime': '.mov',
      'video/x-msvideo': '.avi'
    };
    const ext = extMap[file.type] || '.mp4';
    const fileName = `${userId}_${uuidv4()}${ext}`;
    const storageRef = ref(storage, `${path}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return {
      success: true,
      url: downloadURL,
      path: snapshot.ref.fullPath
    };
  } catch (error) {
    console.error('Error al subir video:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subir fondo de perfil (imagen, GIF o video) - Premium.
 * @param {File} file - Archivo imagen/gif/video
 * @param {string} userId - ID del usuario
 * @returns {Promise<object>}
 */
export const uploadProfileBackground = async (file, userId) => {
  try {
    if (file.size > MAX_SIZES.PROFILE_BACKGROUND) {
      throw new Error(`El archivo debe pesar menos de ${MAX_SIZES.PROFILE_BACKGROUND / (1024 * 1024)}MB`);
    }
    let ext = '.jpg';
    if (file.type === 'image/gif') ext = '.gif';
    else if (file.type.startsWith('image/')) ext = file.name?.match(/\.(webp|png|jpe?g)$/i)?.[0] || '.jpg';
    else if (file.type.startsWith('video/')) {
      const extMap = { 'video/mp4': '.mp4', 'video/webm': '.webm', 'video/quicktime': '.mov' };
      ext = extMap[file.type] || '.mp4';
    }
    const fileName = `${userId}_${uuidv4()}${ext}`;
    const storageRef = ref(storage, `profile_backgrounds/${userId}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return {
      success: true,
      url: downloadURL,
      path: snapshot.ref.fullPath
    };
  } catch (error) {
    console.error('Error al subir fondo de perfil:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Validar archivo de imagen
 * @param {File} file - Archivo a validar
 * @param {number} maxSize - Tamaño máximo en bytes
 * @returns {object}
 */
export const validateImage = (file, maxSize = MAX_SIZES.IMAGE) => {
  const errors = [];
  
  if (!file) {
    errors.push('No se ha seleccionado ningún archivo');
  }
  
  if (file && !file.type.startsWith('image/')) {
    errors.push('El archivo debe ser una imagen');
  }
  
  if (file && file.size > maxSize) {
    errors.push(`La imagen debe pesar menos de ${maxSize / (1024 * 1024)}MB`);
  }
  
  if (file && !STATIC_IMAGE_TYPES.includes(file.type)) {
    errors.push('Formato no soportado. Usa JPG, PNG o WebP');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validar archivo de imagen o GIF (para avatar/banner Premium).
 * @param {File} file - Archivo a validar
 * @param {number} maxSize - Tamaño máximo en bytes (default: MAX_SIZES.GIF)
 * @returns {object} { isValid, errors }
 */
export const validateImageOrGif = (file, maxSize = MAX_SIZES.GIF) => {
  const errors = [];

  if (!file) {
    errors.push('No se ha seleccionado ningún archivo');
  }

  if (file && !file.type.startsWith('image/')) {
    errors.push('El archivo debe ser una imagen o GIF');
  }

  if (file && file.size > maxSize) {
    errors.push(`El archivo debe pesar menos de ${maxSize / (1024 * 1024)}MB`);
  }

  if (file && !IMAGE_OR_GIF_TYPES.includes(file.type)) {
    errors.push('Formato no soportado. Usa JPG, PNG, WebP o GIF');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Subir GIF de perfil (avatar o banner) sin redimensionar.
 * @param {File} file - Archivo GIF
 * @param {string} userId - ID del usuario
 * @param {string} type - 'avatar' | 'banner'
 * @returns {Promise<object>}
 */
export const uploadProfileGif = async (file, userId, type = 'avatar') => {
  try {
    if (file.type !== 'image/gif') {
      return { success: false, error: 'El archivo debe ser un GIF' };
    }
    const maxSize = type === 'banner' ? MAX_SIZES.BANNER : MAX_SIZES.AVATAR;
    if (file.size > maxSize) {
      throw new Error(`El GIF debe pesar menos de ${maxSize / (1024 * 1024)}MB`);
    }
    const folder = type === 'banner' ? 'banners' : 'profile_images';
    const fileName = `${userId}_${uuidv4()}.gif`;
    const storageRef = ref(storage, `${folder}/${userId}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return {
      success: true,
      url: downloadURL,
      path: snapshot.ref.fullPath
    };
  } catch (error) {
    console.error('Error al subir GIF:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
